/**
 * Image resize utility for compressing images before upload
 * Uses WebP format for ~30-50% smaller files than JPEG at equivalent quality
 * Fallback to JPEG for browsers without WebP support
 * Target: max 1600px dimension, ~100-300KB output
 *
 */

/** Standard card / listing photo preset. */
const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_WEBP_QUALITY = 0.82; // WebP at 82% ≈ JPEG at 90% visual quality
const DEFAULT_JPEG_QUALITY = 0.85;
const DEFAULT_MAX_BYTES = 400 * 1024;

/** Tight preset for tiny UI surfaces (avatars, thumbs). */
const AVATAR_MAX_DIMENSION = 512;
const AVATAR_WEBP_QUALITY = 0.8;
const AVATAR_JPEG_QUALITY = 0.82;
const AVATAR_MAX_BYTES = 120 * 1024;

export type ResizePreset = 'card' | 'avatar';

export interface ResizeOptions {
  /** Sizing/quality preset. Defaults to 'card'. */
  preset?: ResizePreset;
}


/**
 * Check if browser supports WebP encoding
 */
function supportsWebP(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

const USE_WEBP = supportsWebP();

interface PresetConfig {
  maxDim: number;
  webpQ: number;
  jpegQ: number;
  maxBytes: number;
}

function getPresetConfig(preset: ResizePreset = 'card'): PresetConfig {
  if (preset === 'avatar') {
    return {
      maxDim: AVATAR_MAX_DIMENSION,
      webpQ: AVATAR_WEBP_QUALITY,
      jpegQ: AVATAR_JPEG_QUALITY,
      maxBytes: AVATAR_MAX_BYTES,
    };
  }
  return {
    maxDim: DEFAULT_MAX_DIMENSION,
    webpQ: DEFAULT_WEBP_QUALITY,
    jpegQ: DEFAULT_JPEG_QUALITY,
    maxBytes: DEFAULT_MAX_BYTES,
  };
}

/**
 * Resizes and compresses an image file
 * - Maintains aspect ratio
 * - Caps at the preset's max dimension on longest side
 * - Uses WebP for better compression (falls back to JPEG)
 * - Progressive quality reduction to fit the preset's byte budget
 */
export async function resizeImage(file: File, options: ResizeOptions = {}): Promise<File> {
  const cfg = getPresetConfig(options.preset);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Calculate new dimensions maintaining aspect ratio
      if (width > cfg.maxDim || height > cfg.maxDim) {
        if (width > height) {
          height = Math.round((height / width) * cfg.maxDim);
          width = cfg.maxDim;
        } else {
          width = Math.round((width / height) * cfg.maxDim);
          height = cfg.maxDim;
        }
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Use high-quality image scaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);


      // Choose format and quality
      const mimeType = USE_WEBP ? 'image/webp' : 'image/jpeg';
      const extension = USE_WEBP ? 'webp' : 'jpg';
      let quality = USE_WEBP ? cfg.webpQ : cfg.jpegQ;

      let blob: Blob | null = null;

      for (let attempt = 0; attempt < 3; attempt++) {
        blob = await new Promise<Blob | null>(res =>
          canvas.toBlob(res, mimeType, quality)
        );

        if (blob && blob.size <= cfg.maxBytes) break;
        quality -= 0.1; // Reduce quality by 10% each attempt
      }

      if (!blob) {
        reject(new Error('Failed to create blob'));
        return;
      }

      // Create new File with appropriate extension
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const newFile = new File([blob], `${baseName}.${extension}`, {
        type: mimeType,
        lastModified: Date.now(),
      });

      resolve(newFile);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Resizes multiple images in sequence to avoid memory issues
 */
export async function resizeImages(files: File[], options: ResizeOptions = {}): Promise<File[]> {
  const resized: File[] = [];
  for (const file of files) {
    const resizedFile = await resizeImage(file, options);
    resized.push(resizedFile);
  }
  return resized;
}

/**
 * Convenience wrapper for mixed-MIME upload paths (chat, contact, inquiry).
 * Runs `resizeImage` on images; passes non-images (PDFs, etc.) through untouched.
 * On resize failure, logs and returns the original file — never blocks the send.
 */
export async function resizeImageIfImage(
  file: File,
  options: ResizeOptions = {}
): Promise<File> {
  if (!file.type || !file.type.startsWith('image/')) return file;
  // Skip formats we can't decode in-browser (e.g. HEIC on non-Safari) — HEIC
  // is already converted upstream by photoInputUtils; anything left is safe.
  try {
    return await resizeImage(file, options);
  } catch (err) {
    console.warn('[resizeImageIfImage] skipped:', err);
    return file;
  }
}

/** Long-lived immutable cache header for CDN-served public bucket uploads. */
export const IMMUTABLE_CACHE_CONTROL = '31536000';

